import axios from "axios";
import * as rax from "retry-axios";
import { RetryHandlerOptions } from "@microsoft/microsoft-graph-client/lib/src/browser";
import type { IGraphCreateShareableLinkRequest } from "../components/_v2/redux/api/graph/api-graph-interfaces";
import type { ISharingLinkSettings } from "./SharePointRestServices";
import { getAuthenticatedClient } from "./GraphClientService";
import { createAbortScope, IAbortScope, isScopeEnded, raceEnded } from "../../../utilities/abortScope";

/**
 * The share panel's two ways of minting a sharing link, each bounded. Graph's gateway gives up on
 * SharePoint after ~30 s and the SDK would replay the 504 three more times (~2.3 min), so Graph gets
 * one attempt with its own time limit, replaying only throttling (429/503) whose wait fits in it, and
 * a gateway time-out only when the caller has no other way to the link. SharePoint's ShareLink is not
 * behind that gateway and can take minutes on a slow tenant; it waits out throttling, retries a
 * gateway error or a dropped connection once (a retry can return the link the first attempt finished
 * creating), and otherwise runs until its scope ends.
 */

export const GRAPH_SHARING_LINK_ATTEMPT_MS = 25000;
const GRAPH_MAX_REPLAYS = 3;
const REST_RETRY_DELAY_MS = 1000;
const REST_MAX_THROTTLING_RETRIES = 3;

export interface IGraphSharingLinkPolicy {
    /** How long this attempt may take, replays included. */
    attemptMs?: number;
    /** Replay a gateway time-out (504). Worth it only without a fallback: each 504 costs Graph's own ~30 s. */
    replayGatewayTimeouts?: boolean;
}

/** A Retry-After header in milliseconds (delta-seconds or an HTTP date); undefined when absent or unreadable. */
export const readRetryAfterMs = (value: string | null | undefined): number | undefined => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return undefined;
    }
    const seconds: number = Number(value);
    if (!Number.isNaN(seconds)) {
        return Math.max(0, seconds * 1000);
    }
    const date: number = Date.parse(value);
    return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
};

export interface IGraphSharingLinkResult {
    webUrl?: string;
    /** HTTP status of a failed attempt; undefined when no response arrived. */
    statusCode?: number;
    code?: string;
    requestId?: string;
    /** The attempt used up its own time limit. */
    timedOut?: boolean;
    /** The caller's scope ended first (panel closed or overall deadline). */
    aborted?: boolean;
    elapsedMs: number;
}

export interface IRestSharingLinkResult {
    url?: string;
    /** HTTP status of the final failed answer; undefined when no response arrived. */
    status?: number;
    /** SharePoint's own explanation of the failure, when it sent one. */
    message?: string;
    aborted?: boolean;
    elapsedMs: number;
}

const readStatusCode = (error: any): number | undefined =>
    typeof error?.statusCode === "number" && error.statusCode > 0 ? error.statusCode : undefined;

/** One bounded Graph createLink attempt (beta: `recipients` is beta-only). Never throws. */
export const requestGraphSharingLink = async (
    request: IGraphCreateShareableLinkRequest,
    accessToken: string,
    parent?: IAbortScope,
    policy: IGraphSharingLinkPolicy = {}
): Promise<IGraphSharingLinkResult> => {
    const startedAt: number = Date.now();
    const attemptMs: number = policy.attemptMs ?? GRAPH_SHARING_LINK_ATTEMPT_MS;
    const attemptDeadline: number = startedAt + attemptMs;
    const attempt: IAbortScope = createAbortScope(attemptMs, parent);
    const elapsed = (): number => Date.now() - startedAt;
    const interrupted = (): IGraphSharingLinkResult => attempt.endReason() === "timeout"
        ? { timedOut: true, elapsedMs: elapsed() }
        : { aborted: true, elapsedMs: elapsed() };
    // The SDK retries 429, 503 and 504. Replay only when the wait still ends inside this attempt.
    const shouldReplay = (_delay: number, replaysSoFar: number, _request: any, _options: any, response: Response): boolean => {
        if (response.status === 504 && !policy.replayGatewayTimeouts) {
            return false;
        }
        // Without Retry-After the SDK waits up to 1 s, then 2-3 s, then 4-5 s (RetryHandler.getDelay with delay 0).
        const next: number = replaysSoFar + 1;
        const waitMs: number = readRetryAfterMs(response.headers?.get("Retry-After"))
            ?? ((next >= 2 ? Math.round(0.5 * (Math.pow(2, next) - 1)) : 0) + 1) * 1000;
        return Date.now() + waitMs < attemptDeadline;
    };
    try {
        let graphRequest = getAuthenticatedClient(accessToken)
            .api(`/drives/${request.driveId}/items/${request.itemId}/createLink`)
            .version("beta")
            .middlewareOptions([new RetryHandlerOptions(0, GRAPH_MAX_REPLAYS, shouldReplay)]);
        if (attempt.signal) {
            graphRequest = graphRequest.option("signal", attempt.signal);
        }
        const content = {
            type: request.type,
            scope: request.scope,
            recipients: request.recipients?.map(recipient => ({ email: recipient }))
        };
        const permission: any = await raceEnded(graphRequest.post(content), attempt);
        if (isScopeEnded(permission)) {
            return interrupted();
        }
        const webUrl: unknown = permission?.link?.webUrl;
        return typeof webUrl === "string" && webUrl.trim().length > 0
            ? { webUrl, elapsedMs: elapsed() }
            : { elapsedMs: elapsed() };
    } catch (error) {
        if (attempt.endReason()) {
            return interrupted();
        }
        return { statusCode: readStatusCode(error), code: error?.code, requestId: error?.requestId, elapsedMs: elapsed() };
    } finally {
        attempt.dispose();
    }
};

/** SharePoint's explanation in an odata=verbose or odata=minimal error body, if there is one. */
export const readSharePointErrorMessage = (error: any): string | undefined => {
    const data = error?.response?.data;
    const value: unknown = data?.error?.message?.value ?? data?.["odata.error"]?.message?.value;
    return typeof value === "string" && value.trim().length > 0 ? value : undefined;
};

/** The ShareLink endpoint and body for a list item addressed by its UniqueId; undefined when it can't be addressed. */
export const buildShareLinkRequest = (
    webUrl: string,
    listId: string,
    itemUniqueId: string,
    settings: ISharingLinkSettings,
    peoplePickerInput?: string
): { url: string; body: any } | undefined => {
    const normalizedListId: string = listId?.replace("{", "").replace("}", "");
    const normalizedUniqueId: string = itemUniqueId?.replace("{", "").replace("}", "");
    if (!webUrl || !normalizedListId || !normalizedUniqueId) {
        return undefined;
    }
    return {
        url: `${webUrl}/_api/web/Lists('${normalizedListId}')/GetItemByUniqueId('${normalizedUniqueId}')/ShareLink`,
        body: {
            request: {
                createLink: true,
                settings,
                ...(peoplePickerInput ? { peoplePickerInput } : {})
            }
        }
    };
};

/** The link URL in a ShareLink response, across odata shapes; undefined for a missing or blank Url. */
export const readSharingLinkUrl = (data: any): string | undefined => {
    const sharingLinkInfo =
        data?.d?.ShareLink?.sharingLinkInfo
        ?? data?.d?.sharingLinkInfo
        ?? data?.ShareLink?.sharingLinkInfo
        ?? data?.sharingLinkInfo;
    return sharingLinkInfo?.Url || undefined;
};

// A private instance, so this retry policy never mixes with the app-wide axios defaults.
const restClient = axios.create();
rax.attach(restClient);

/**
 * Whether a failed ShareLink request is asked again: throttling (429/503) up to three times, a gateway
 * error (502/504) or a dropped connection once, and only while the wait ends before `deadlineAt`,
 * judged when the answer arrives.
 */
const shouldRetryShareLink = (error: any, scope: IAbortScope | undefined, deadlineAt: number | undefined): boolean => {
    const config = rax.getConfig(error);
    if (!config || scope?.endReason()) {
        return false;
    }
    const status: number | undefined = error?.response?.status;
    let allowedRetries: number;
    if (status === undefined) {
        allowedRetries = 1;
    } else if (status === 429 || status === 503) {
        allowedRetries = REST_MAX_THROTTLING_RETRIES;
    } else if (status === 502 || status === 504) {
        allowedRetries = 1;
    } else {
        return false;
    }
    if ((config.currentRetryAttempt ?? 0) >= allowedRetries) {
        return false;
    }
    const waitMs: number = readRetryAfterMs(error?.response?.headers?.["retry-after"]) ?? REST_RETRY_DELAY_MS;
    return deadlineAt === undefined || Date.now() + waitMs < deadlineAt;
};

/**
 * One ShareLink request with the retries of shouldRetryShareLink. Runs until `scope` ends. Never throws.
 */
export const requestRestSharingLink = async (
    accessToken: string,
    webUrl: string,
    listId: string,
    itemUniqueId: string,
    settings: ISharingLinkSettings,
    peoplePickerInput?: string,
    scope?: IAbortScope,
    deadlineAt?: number
): Promise<IRestSharingLinkResult> => {
    const startedAt: number = Date.now();
    const elapsed = (): number => Date.now() - startedAt;
    const shareLink = buildShareLinkRequest(webUrl, listId, itemUniqueId, settings, peoplePickerInput);
    if (!shareLink) {
        return { elapsedMs: elapsed() };
    }
    try {
        const response: any = await raceEnded(restClient.post(shareLink.url, shareLink.body, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                accept: "application/json;odata=verbose",
                "content-type": "application/json;odata=verbose"
            },
            signal: scope?.signal,
            raxConfig: {
                instance: restClient,
                retry: REST_MAX_THROTTLING_RETRIES,
                noResponseRetries: 1,
                httpMethodsToRetry: ["POST"],
                statusCodesToRetry: [[429, 429], [502, 504]],
                backoffType: "static",
                retryDelay: REST_RETRY_DELAY_MS,
                // shouldRetry makes the real Retry-After decision when the answer arrives; this cap only
                // has to let those waits through (retry-axios rejects any Retry-After above it).
                maxRetryAfter: deadlineAt !== undefined ? Math.max(1, deadlineAt - Date.now()) : undefined,
                shouldRetry: (error: any) => shouldRetryShareLink(error, scope, deadlineAt)
            }
        } as any), scope);
        if (isScopeEnded(response)) {
            return { aborted: true, elapsedMs: elapsed() };
        }
        return { url: readSharingLinkUrl(response?.data), elapsedMs: elapsed() };
    } catch (error) {
        if (scope?.endReason() || axios.isCancel(error)) {
            return { aborted: true, elapsedMs: elapsed() };
        }
        return { status: error?.response?.status, message: readSharePointErrorMessage(error), elapsedMs: elapsed() };
    }
};
